import React, {useCallback, useEffect, useState} from 'react';
import {Controller} from 'react-hook-form';
import {FormError, FormLabel, getInputWrapperClass, getWholeDivWrapperClass} from './sharedStyles.tsx';
import {AlertCircle, Download, Eye, FileText, FileUp, X} from 'lucide-react';
import {CUSTOM_ERROR_TYPE, FileUploadComponent, FormComponentProps, ValidationRuleLevel} from '../DynamicFormTypes.ts';
import {FormSubmissionGeneralFileMeta, GeneralFile, GeneralFileType} from "../../../api/ApiTypes.ts";
import {useFormFiles, useFormMethods, useFormStatic} from "../DyanmicFormContext.tsx";
import {FileDownloadType} from "../../FileViewer.tsx";
import {setRuleType} from "../dyanimcFormUtil.tsx";
import {useValidationRules} from "../hooks/useValidationRules.tsx";

const FileUploadRenderer: React.FC<FormComponentProps<FileUploadComponent>> = ({component}) => {

    const { formMethods } = useFormMethods()
    const { formPermissions } = useFormStatic();
    const {generalFiles, addGeneralFile, removeGeneralFile, viewFile, downloadFile} = useFormFiles();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fieldFiles, setFieldFiles] = useState<GeneralFile[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Add safeguards
    const fieldKey = component.fieldKey;
    const control = formMethods.control;

    // Filter files associated with this field
    useEffect(() => {
        try {
            // Extract files associated with this field
            const associatedFiles = generalFiles.filter(file => {
                if (!file || !file.metaData) return false;

                let meta: FormSubmissionGeneralFileMeta;

                // Handle the case where metaData might be a string instead of an object
                if (typeof file.metaData === 'string') {
                    try {
                        meta = JSON.parse(file.metaData) as FormSubmissionGeneralFileMeta;
                    } catch (e) {
                        console.error('Error parsing metadata string:', e);
                        return false;
                    }
                } else {
                    meta = file.metaData as FormSubmissionGeneralFileMeta;
                }

                console.log('Parsed metadata:', meta);

                return meta.fieldId === fieldKey;
            });

            setFieldFiles(associatedFiles);

            // Log for debugging
            console.log(`Files for field ${fieldKey}:`, associatedFiles);

            // Update form value with file IDs (if needed)
            if (associatedFiles.length > 0) {
                const fileIds = associatedFiles.map(file => file.id);
                // Only update if current value is different to avoid loops
                const currentValue = formMethods.getValues(fieldKey);
                if (JSON.stringify(currentValue) !== JSON.stringify(fileIds)) {
                    if (!formPermissions.canEdit) {
                        console.info("Edits or auto-set values not allowed, skipping form value update for file", fieldKey, currentValue, fileIds);
                        return;
                    }
                    formMethods.setValue(fieldKey, fileIds);
                }
            }
            else {
                // If no files, ensure the field is cleared
                const currentValue = formMethods.getValues(fieldKey);
                if (currentValue && currentValue.length > 0) {
                    if (!formPermissions.canEdit) {
                        console.info("Edits or auto-set values not allowed, skipping form value update for file", fieldKey, currentValue, []);
                        return;
                    }
                    formMethods.setValue(fieldKey, []);
                }
            }
        } catch (err) {
            console.error("Error processing files:", err);
            setError("Error loading files. Please refresh the page.");
        }
    }, [generalFiles, fieldKey, formMethods, formPermissions.canEdit]);

    // Build validation rules with safety checks
    const validate = useValidationRules({ component });
    // Handle file upload
    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const fileList = Array.from(event.target.files);
        console.log("Selected files:", fileList.map(f => f.name));

        if (component.allowedFileTypes && component.allowedFileTypes.length > 0) {
            const invalidFiles = fileList.filter(file => {
                const extension = file.name.split('.').pop()?.toLowerCase();
                return !component.allowedFileTypes?.includes(extension || '');
            });

            if (invalidFiles.length > 0) {
                const allowedTypes = component.allowedFileTypes.join(', ');
                formMethods.setError(fieldKey, {
                    type: setRuleType(ValidationRuleLevel.ERROR, CUSTOM_ERROR_TYPE.MANUAL),
                    message: `Only ${allowedTypes} files are allowed`
                });
                return;
            }
            // Clear any previous error for this field
            formMethods.clearErrors(fieldKey);
        }

        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        try {
            // Simulate progress
            const interval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 300);

            const fileMeta: FormSubmissionGeneralFileMeta = {
                type: GeneralFileType.FORM_SUBMISSION,
                fieldId: fieldKey,
                fileType: component.fileType
            };

            console.log("Uploading with metadata:", fileMeta);

            // Upload each file
            const uploadedFiles: GeneralFile[] = [];
            for (const file of fileList) {
                try {
                    const newFile = await addGeneralFile(file, fileMeta);
                    if (newFile) {
                        uploadedFiles.push(newFile);
                    }
                } catch (err) {
                    console.error(`Error uploading file ${file.name}:`, err);
                }
            }

            clearInterval(interval);
            setUploadProgress(100);

            // Log successful uploads
            console.log("Successfully uploaded files:", uploadedFiles.map(f => f.fileName));

            // Reset after completion
            setTimeout(() => {
                setUploadProgress(0);
                setIsUploading(false);

                // Clear the file input
                if (event.target) {
                    event.target.value = '';
                }
            }, 1000);

        } catch (error) {
            console.error('Upload failed:', error);
            setError('File upload failed. Please try again.');
            formMethods.setError(fieldKey, {
                type: setRuleType(ValidationRuleLevel.ERROR, CUSTOM_ERROR_TYPE.MANUAL),
                message: 'File upload failed. Please try again.'
            });
            setIsUploading(false);
            setUploadProgress(0);

            // Clear the file input
            if (event.target) {
                event.target.value = '';
            }
        }
    }, [component.allowedFileTypes, component.fileType, fieldKey]);

    // Handle file removal
    const handleRemoveFile = useCallback(async (fileId: number) => {
        try {
            console.log(`Removing file with ID ${fileId}`);
            await removeGeneralFile(fileId);
            // File list will be updated by the effect when generalFiles changes
        } catch (error) {
            console.error('File removal failed:', error);
            setError('Failed to remove file. Please try again.');
            formMethods.setError(fieldKey, {
                type: setRuleType(ValidationRuleLevel.ERROR, CUSTOM_ERROR_TYPE.MANUAL),
                message: 'Failed to remove file. Please try again.'
            });
        }
    }, [removeGeneralFile]);

    // View file
    const handleViewFile = useCallback(async (fileId: number) => {
        try {
            await viewFile(fileId, FileDownloadType.GENERAL_FILE);
        } catch (error) {
            console.error('Failed to view file:', error);
            setError('Failed to view file. Please try again.');
        }
    }, [viewFile]);

    // Download file
    const handleDownloadFile = useCallback(async (fileId: number) => {
        try {
            await downloadFile(fileId, FileDownloadType.GENERAL_FILE);
        } catch (error) {
            console.error('Failed to download file:', error);
            setError('Failed to download file. Please try again.');
        }
    }, [downloadFile]);

    return (
        <Controller
            name={fieldKey}
            control={control}
            rules={validate}
            render={({ field, fieldState }) => (
                <div className={getWholeDivWrapperClass(component)}>
                    <FormLabel component={component} />

                    <div className={getInputWrapperClass(component)}>
                        {/* Hidden input for form state */}
                        <input
                            type="hidden"
                            {...field}
                            value={field.value || ''}
                        />

                        {/* Error message */}
                        {error && (
                            <div className="mb-3 p-2 bg-red-50 border border-red-300 rounded-md flex items-start">
                                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        )}

                        {/* File Upload Area */}
                        <label className={`
                            border-2 border-dashed rounded-md p-4 block text-center 
                            cursor-pointer transition-colors
                            ${fieldState.error
                            ? 'border-red-300 bg-red-50 hover:bg-red-100'
                            : 'border-blue-200 hover:bg-blue-50'}
                            ${isUploading ? 'pointer-events-none opacity-60' : ''}
                        `}>
                            <FileUp className="h-8 w-8 mx-auto text-blue-400 mb-2" />

                            <p className="text-sm text-gray-600 mb-1">
                                {'Click to select a file to upload'}
                            </p>

                            {component.allowedFileTypes && (
                                <p className="text-xs text-gray-500">
                                    Allowed: {component.allowedFileTypes.join(', ')}
                                </p>
                            )}

                            <input
                                id={fieldKey}
                                type="file"
                                className="hidden"
                                onChange={handleFileChange}
                                disabled={isUploading}
                                multiple={true}
                            />
                        </label>

                        {/* Upload Progress */}
                        {isUploading && (
                            <div className="mt-3">
                                <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-300 ease-out"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                                <p className="text-xs text-right text-gray-500 mt-1">
                                    {uploadProgress}% uploaded
                                </p>
                            </div>
                        )}

                        {/* File List */}
                        {fieldFiles.length > 0 && (
                            <div className="mt-3">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Uploaded Files ({fieldFiles.length})
                                </h4>
                                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden">
                                    {fieldFiles.map((file, index) => (
                                        <li key={`file-${file.id}-${index}`}
                                            className="p-2 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                                            <div className="flex items-center overflow-hidden">
                                                <FileText className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                                                <span className="text-sm text-gray-700 truncate">
                                                    {file.fileName}
                                                </span>
                                            </div>
                                            <div className="flex space-x-1 ml-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewFile(file.id)}
                                                    className="p-1.5 rounded-full text-blue-500 hover:bg-blue-50"
                                                    title="View file"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDownloadFile(file.id)}
                                                    className="p-1.5 rounded-full text-green-500 hover:bg-green-50"
                                                    title="Download file"
                                                >
                                                    <Download className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveFile(file.id)}
                                                    className="p-1.5 rounded-full text-red-500 hover:bg-red-50"
                                                    title="Remove file"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <FormError fieldState={fieldState} component={component} />
                </div>
            )}
        />
    );
}
export const FileUpload = React.memo(FileUploadRenderer);